import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const popIn = keyframes`
  0%   { transform: scale(0);    opacity: 0; }
  70%  { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(28px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

const slideInFromBottom = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const slideRight = keyframes`
  from { width: 0; }
  to   { width: var(--bar-width); }
`;

const ripplePulse = keyframes`
  0%   { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%       { transform: translateY(-6px) rotate(1deg); }
  75%       { transform: translateY(4px) rotate(-1deg); }
`;

const shimmerSlide = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0   rgba(37,99,235,0.18); }
  50%       { box-shadow: 0 0 0 8px rgba(37,99,235,0);    }
`;

const checkDraw = keyframes`
  from { stroke-dashoffset: 24; }
  to   { stroke-dashoffset: 0;  }
`;

const orbitSpin = keyframes`
  from { transform: rotate(0deg)   translateX(28px) rotate(0deg); }
  to   { transform: rotate(360deg) translateX(28px) rotate(-360deg); }
`;

const mergePulse = keyframes`
  0%, 100% { r: 3; }
  50%       { r: 4.5; }
`;

const pathDash = keyframes`
  from { stroke-dashoffset: 30; }
  to   { stroke-dashoffset: 0;  }
`;

// ═══════════════════════════════════════════════════════════════
// BREAKPOINTS
// ═══════════════════════════════════════════════════════════════

const bp = {
  xs: '360px',
  sm: '480px',
  md: '640px',
  lg: '768px',
  xl: '1024px',
};

// ═══════════════════════════════════════════════════════════════
// LAYOUT
// ═══════════════════════════════════════════════════════════════

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f0f6ff;
  padding: 20px 16px 100px;
  overflow-x: hidden;

  @media (min-width: ${bp.lg}) {
    padding: 36px 24px 80px;
  }
`;

const Container = styled.div`
  max-width: 820px;
  margin: 0 auto;
  animation: ${fadeUp} 0.5s ease-out both;
`;

// ═══════════════════════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════════════════════

const HeroSection = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #0c2d48 0%, #1e40af 40%, #0369a1 70%, #0ea5e9 100%);
  padding: 36px 28px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(37,99,235,0.18);

  @media (max-width: ${bp.sm}) {
    padding: 28px 20px;
    border-radius: 18px;
  }

  @media (min-width: ${bp.lg}) {
    padding: 48px 52px;
    border-radius: 22px;
    margin-bottom: 32px;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
  }
`;

const HeroGrid = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: ${bp.lg}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
  }
`;

const HeroLeft = styled.div`
  flex: 1;

  @media (max-width: ${bp.sm}) {
    text-align: center;
  }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 100px;
  padding: 5px 14px 5px 7px;
  margin-bottom: 16px;

  @media (max-width: ${bp.xs}) {
    padding-right: 10px;
  }
`;

const BadgeDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #67e8f9;
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: #67e8f9;
    animation: ${ripplePulse} 2s ease-out infinite;
  }
`;

const BadgeText = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255,255,255,0.92);
  letter-spacing: 0.3px;
`;

const HeroTitle = styled.h1`
  font-size: 1.7rem;
  font-weight: 800;
  color: white;
  margin: 0 0 10px;
  letter-spacing: -0.5px;
  line-height: 1.2;

  @media (min-width: ${bp.lg}) {
    font-size: 2.1rem;
  }
`;

const HeroDesc = styled.p`
  font-size: 0.9rem;
  color: rgba(255,255,255,0.78);
  margin: 0;
  line-height: 1.65;
  max-width: 420px;

  @media (max-width: ${bp.sm}) {
    max-width: none;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 1rem;
  }
`;

const HeroVisual = styled.div`
  flex-shrink: 0;
  width: 130px;
  height: 110px;
  align-self: center;
  animation: ${float} 6s ease-in-out infinite;

  @media (min-width: ${bp.lg}) {
    width: 180px;
    height: 150px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// STEP INDICATOR
// ═══════════════════════════════════════════════════════════════

const StepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 22px;
  flex-wrap: wrap;

  @media (min-width: ${bp.lg}) {
    gap: 14px;
    margin-bottom: 28px;
  }
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;

  .num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 800;
    transition: all 0.3s ease;
    background: ${p => p.active
      ? 'linear-gradient(135deg, #2563eb, #0ea5e9)'
      : p.completed
        ? '#2563eb'
        : '#e2e8f0'};
    color: ${p => (p.active || p.completed) ? 'white' : '#94a3b8'};

    svg {
      width: 13px;
      height: 13px;
      stroke: white;
      fill: none;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 24;
      stroke-dashoffset: 0;
      animation: ${checkDraw} 0.4s ease forwards;
    }
  }

  .label {
    font-size: 0.78rem;
    font-weight: 700;
    color: ${p => p.active
      ? '#1e40af'
      : p.completed
        ? '#2563eb'
        : '#94a3b8'};
    transition: color 0.3s ease;

    @media (min-width: ${bp.lg}) {
      font-size: 0.82rem;
    }
  }
`;

const StepLine = styled.div`
  width: 28px;
  height: 2px;
  border-radius: 2px;
  background: ${p => p.active ? '#2563eb' : '#e2e8f0'};
  transition: background 0.3s ease;

  @media (min-width: ${bp.lg}) {
    width: 48px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// TESTS PANEL
// ═══════════════════════════════════════════════════════════════

const TestsPanel = styled.div`
  background: white;
  border-radius: 16px;
  border: 1.5px solid #e8f0fe;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  overflow: hidden;
  margin-bottom: 0;

  @media (min-width: ${bp.lg}) {
    border-radius: 18px;
  }
`;

const TestsPanelHeader = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  @media (min-width: ${bp.lg}) {
    padding: 20px 24px;
  }
`;

const PanelTitle = styled.h2`
  font-size: 0.95rem;
  font-weight: 800;
  color: #0c2d48;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  .icon-wrap {
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, #dbeafe, #bae6fd);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 15px;
      height: 15px;
      stroke: #1d4ed8;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  @media (min-width: ${bp.lg}) {
    font-size: 1rem;

    .icon-wrap { width: 30px; height: 30px; svg { width: 16px; height: 16px; } }
  }
`;

const CountBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  padding: 3px 10px;
  border-radius: 100px;
`;

const SearchBar = styled.div`
  padding: 0 18px 14px;
  display: flex;
  gap: 10px;

  @media (max-width: ${bp.sm}) {
    flex-direction: column;
  }

  @media (min-width: ${bp.lg}) {
    padding: 0 24px 16px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: 1.5px solid #dbeafe;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.84rem;
  color: #1e3a5f;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
  }
`;

const SearchBtn = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #2563eb, #0369a1);
  cursor: pointer;
  white-space: nowrap;
  min-height: 42px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── MSE standalone block ───────────────────────────────────────

const MseBlock = styled.div`
  border-bottom: 1px solid #f1f5f9;
`;

const MseRow = styled.div`
  padding: 14px 18px 14px 56px;
  cursor: pointer;
  transition: background 0.18s;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: ${p => p.selected
    ? 'linear-gradient(90deg,#eff6ff,#f8fafc)'
    : 'transparent'};

  &:hover { background: rgba(37,99,235,0.03); }

  /* Custom checkbox */
  &::before {
    content: '';
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border: 2px solid ${p => p.selected ? '#2563eb' : '#e2e8f0'};
    border-radius: 6px;
    background: ${p => p.selected
      ? 'linear-gradient(135deg,#2563eb,#0369a1)'
      : 'white'};
    transition: all 0.2s;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 25px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 6px;
    height: 10px;
    border: 2.5px solid white;
    border-top: none;
    border-left: none;
    opacity: ${p => p.selected ? 1 : 0};
    transition: opacity 0.2s;
    pointer-events: none;
  }

  .info { flex: 1; min-width: 0; }

  .name {
    font-size: 0.88rem;
    font-weight: 700;
    color: ${p => p.selected ? '#1e40af' : '#0c2d48'};
    margin: 0 0 2px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .desc {
    font-size: 0.76rem;
    color: #94a3b8;
    margin: 0;
    line-height: 1.4;
  }

  .price {
    font-size: 0.8rem;
    font-weight: 700;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 3px 10px;
    border-radius: 6px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  @media (max-width: ${bp.sm}) {
    align-items: flex-start;
    padding: 14px 14px 14px 52px;

    .price {
      align-self: flex-start;
      margin-top: 2px;
    }
  }

  @media (min-width: ${bp.lg}) {
    padding: 16px 24px 16px 62px;

    &::before { left: 22px; }
    &::after  { left: 30px; }

    .name { font-size: 0.92rem; }
  }
`;

const MseBadge = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  color: white;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const MseHeader = styled.div`
  padding: 10px 18px 6px;
  display: flex;
  align-items: center;
  gap: 8px;

  .lbl {
    font-size: 0.68rem;
    font-weight: 800;
    color: #7c3aed;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, #e9d5ff, transparent);
  }

  @media (min-width: ${bp.lg}) {
    padding: 12px 24px 6px;
  }
`;

// ─── Category blocks ────────────────────────────────────────────

const CategoryBlock = styled.div`
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const CategoryHead = styled.button`
  width: 100%;
  padding: 14px 18px;
  background: ${p => p.expanded
    ? 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)'
    : 'transparent'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.25s ease;
  border-left: 3px solid ${p => p.expanded ? '#2563eb' : 'transparent'};
  touch-action: manipulation;

  &:active { background: #f0f9ff; }

  @media (hover: hover) {
    &:hover {
      background: ${p => p.expanded
        ? 'linear-gradient(135deg,#dbeafe 0%,#e0f2fe 100%)'
        : '#f8fafc'};
    }
  }

  @media (min-width: ${bp.lg}) {
    padding: 16px 24px;
  }
`;

const CatLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  flex: 1;
  min-width: 0;
`;

const CatIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  background: ${p => {
    if (p.expanded) return 'linear-gradient(135deg,#2563eb,#0369a1)';
    switch (p.catId) {
      case 'disorders':   return 'linear-gradient(135deg,#fef2f2,#fee2e2)';
      case 'cbt':         return 'linear-gradient(135deg,#eff6ff,#dbeafe)';
      case 'personality': return 'linear-gradient(135deg,#fdf4ff,#f3e8ff)';
      case 'wellbeing':   return 'linear-gradient(135deg,#f0fdf4,#dcfce7)';
      case 'social':      return 'linear-gradient(135deg,#fff7ed,#ffedd5)';
      case 'resilience':  return 'linear-gradient(135deg,#fefce8,#fef9c3)';
      case 'mood':        return 'linear-gradient(135deg,#fff1f2,#fce7f3)';
      case 'development': return 'linear-gradient(135deg,#f0fdfa,#ccfbf1)';
      default:            return 'linear-gradient(135deg,#f8fafc,#f1f5f9)';
    }
  }};

  svg {
    width: 19px;
    height: 19px;
    stroke: ${p => {
      if (p.expanded) return 'white';
      switch (p.catId) {
        case 'disorders':   return '#ef4444';
        case 'cbt':         return '#2563eb';
        case 'personality': return '#9333ea';
        case 'wellbeing':   return '#16a34a';
        case 'social':      return '#ea580c';
        case 'resilience':  return '#ca8a04';
        case 'mood':        return '#db2777';
        case 'development': return '#0d9488';
        default:            return '#64748b';
      }
    }};
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: stroke 0.25s ease;
  }

  @media (min-width: ${bp.lg}) {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    svg { width: 21px; height: 21px; }
  }
`;

const CatText = styled.div`
  flex: 1;
  min-width: 0;

  .cat-name {
    font-size: 0.88rem;
    font-weight: 750;
    color: ${p => p.expanded ? '#1e40af' : '#0c2d48'};
    margin: 0 0 2px;
    transition: color 0.25s;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cat-count {
    font-size: 0.74rem;
    color: ${p => p.expanded ? '#2563eb' : '#94a3b8'};
    font-weight: 600;
    margin: 0;
    transition: color 0.25s;
  }

  @media (min-width: ${bp.lg}) {
    .cat-name  { font-size: 0.92rem; }
    .cat-count { font-size: 0.78rem; }
  }
`;

const Chevron = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${p => p.expanded ? '#2563eb' : '#94a3b8'};
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), color 0.25s;
  transform: ${p => p.expanded ? 'rotate(180deg)' : 'rotate(0)'};

  svg {
    width: 100%;
    height: 100%;
    stroke: currentColor;
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const CategoryBody = styled.div`
  display: ${p => (p.expanded ? 'block' : 'none')};
  background: ${p => p.expanded ? '#fafcff' : 'transparent'};
`;

const TestRow = styled.div`
  padding: 13px 18px 13px 56px;
  cursor: pointer;
  transition: background 0.18s;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: ${p => p.selected
    ? 'linear-gradient(90deg,#eff6ff,#f8fafc)'
    : 'transparent'};
  touch-action: manipulation;

  &:active { background: #eff6ff; }

  @media (hover: hover) {
    &:hover { background: rgba(37,99,235,0.03); }
  }

  /* Custom checkbox */
  &::before {
    content: '';
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border: 2px solid ${p => p.selected ? '#2563eb' : '#e2e8f0'};
    border-radius: 6px;
    background: ${p => p.selected
      ? 'linear-gradient(135deg,#2563eb,#0369a1)'
      : 'white'};
    transition: all 0.2s;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 25px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 5px;
    height: 9px;
    border: 2.5px solid white;
    border-top: none;
    border-left: none;
    opacity: ${p => p.selected ? 1 : 0};
    transition: opacity 0.2s;
    pointer-events: none;
  }

  .test-info { flex: 1; min-width: 0; }

  .test-name {
    font-size: 0.85rem;
    font-weight: 650;
    color: ${p => p.selected ? '#1e40af' : '#1e3a5f'};
    margin: 0 0 2px;
    line-height: 1.3;
    transition: color 0.2s;
  }

  .test-desc {
    font-size: 0.74rem;
    color: #94a3b8;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .test-price {
    font-size: 0.78rem;
    font-weight: 700;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 3px 10px;
    border-radius: 6px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  @media (max-width: ${bp.sm}) {
    align-items: flex-start;
    padding: 13px 14px 13px 52px;

    .test-price {
      align-self: flex-start;
      margin-top: 2px;
    }
  }

  @media (min-width: ${bp.lg}) {
    padding: 14px 24px 14px 62px;

    &::before { left: 22px; }
    &::after  { left: 30px; }

    .test-name  { font-size: 0.88rem; }
    .test-desc  { font-size: 0.76rem; }
    .test-price { font-size: 0.8rem; }
  }
`;

// ═══════════════════════════════════════════════════════════════
// TRANSITION CONNECTOR
// ═══════════════════════════════════════════════════════════════

const ConnectorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 0;
  opacity: ${p => p.visible ? 1 : 0};
  transform: ${p => p.visible ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.4s cubic-bezier(0.4,0,0.2,1) 0.1s;
  pointer-events: none;
`;

const ConnectorLine = styled.div`
  width: 2px;
  height: 22px;
  background: linear-gradient(to bottom, #bfdbfe, #2563eb);
  border-radius: 100px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2563eb;
    animation: ${glowPulse} 2s ease-in-out infinite;
  }
`;

const ConnectorLabel = styled.div`
  margin-top: 8px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  background: linear-gradient(90deg, #2563eb, #0ea5e9, #2563eb);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmerSlide} 3s linear infinite;
`;

// ═══════════════════════════════════════════════════════════════
// FORM PANEL
// ═══════════════════════════════════════════════════════════════

const FormPanelWrapper = styled.div`
  max-height: ${p => p.visible ? '1400px' : '0'};
  opacity: ${p => p.visible ? 1 : 0};
  overflow: hidden;
  transform: ${p => p.visible ? 'translateY(0)' : 'translateY(-16px)'};
  transition:
    max-height 0.5s cubic-bezier(0.4,0,0.2,1),
    opacity    0.4s ease,
    transform  0.4s ease;
`;

const FormPanel = styled.div`
  background: white;
  border-radius: 16px;
  border: 1.5px solid #e8f0fe;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  padding: 22px 18px;
  margin-top: 0;

  @media (min-width: ${bp.lg}) {
    border-radius: 18px;
    padding: 28px 28px;
  }
`;

const FormSectionTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 800;
  color: #0c2d48;
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  .icon-wrap {
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, #2563eb, #0369a1);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 14px;
      height: 14px;
      stroke: white;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  @media (min-width: ${bp.lg}) {
    font-size: 1rem;
    .icon-wrap { width: 30px; height: 30px; svg { width: 15px; height: 15px; } }
  }
`;

const SelectedBanner = styled.div`
  background: linear-gradient(135deg, #eff6ff, #f0f9ff);
  border: 1.5px solid #93c5fd;
  border-radius: 12px;
  padding: 14px 14px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  animation: ${scaleIn} 0.35s cubic-bezier(0.22,1,0.36,1);

  .check-icon {
    width: 28px;
    height: 28px;
    background: #2563eb;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    animation: ${popIn} 0.4s ease;
    margin-top: 1px;

    svg {
      width: 13px;
      height: 13px;
      stroke: white;
      fill: none;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .text {
    flex: 1;
    min-width: 0;

    .label {
      font-size: 0.66rem;
      font-weight: 800;
      color: #1d4ed8;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin: 0 0 3px;
    }

    .name {
      font-size: 0.86rem;
      font-weight: 700;
      color: #0c2d48;
      margin: 0;
      line-height: 1.3;
    }

    .sub-list {
      margin-top: 5px;
      font-size: 0.72rem;
      color: #64748b;
      line-height: 1.6;
    }
  }

  .price-wrap {
    flex-shrink: 0;
    text-align: right;

    .price-lbl {
      font-size: 0.66rem;
      color: #94a3b8;
      margin: 0 0 2px;
    }

    .price-val {
      font-size: 1.05rem;
      font-weight: 800;
      color: #2563eb;
    }
  }

  .change-btn {
    flex-shrink: 0;
    background: rgba(37,99,235,0.08);
    border: 1px solid rgba(37,99,235,0.2);
    color: #2563eb;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 5px 11px;
    border-radius: 7px;
    cursor: pointer;
    transition: all 0.18s;
    white-space: nowrap;
    align-self: flex-start;
    touch-action: manipulation;

    &:active { background: rgba(37,99,235,0.15); }

    @media (hover: hover) {
      &:hover { background: rgba(37,99,235,0.15); border-color: rgba(37,99,235,0.35); }
    }
  }

  @media (max-width: ${bp.sm}) {
    flex-wrap: wrap;

    .text {
      flex-basis: calc(100% - 40px);
    }

    .price-wrap {
      text-align: left;
    }
  }

  @media (min-width: ${bp.lg}) {
    padding: 16px 16px;
    align-items: center;

    .text .name { font-size: 0.9rem; }
    .price-wrap .price-val { font-size: 1.1rem; }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
  animation: ${fadeUp} 0.35s ease backwards;
  animation-delay: ${p => p.delay || '0s'};

  @media (min-width: ${bp.lg}) {
    margin-bottom: 20px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.76rem;
  font-weight: 750;
  color: #475569;
  margin-bottom: 7px;
  letter-spacing: 0.2px;

  .req { color: #ef4444; margin-left: 2px; }
`;

const RadioCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: ${bp.xs}) {
    grid-template-columns: 1fr;
  }
`;

const RadioCard = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  padding: 16px 10px;
  border-radius: 12px;
  border: 2px solid ${p => p.selected ? '#2563eb' : '#e2e8f0'};
  background: ${p => p.selected
    ? 'linear-gradient(135deg,#eff6ff,#f0f9ff)'
    : 'white'};
  transition: all 0.22s ease;
  text-align: center;
  touch-action: manipulation;

  input { display: none; }

  &:active { border-color: #2563eb; }

  @media (hover: hover) {
    &:hover {
      border-color: ${p => p.selected ? '#2563eb' : '#93c5fd'};
      background: ${p => p.selected
        ? 'linear-gradient(135deg,#eff6ff,#f0f9ff)'
        : '#f0f9ff'};
    }
  }

  .card-icon {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.selected ? '#2563eb' : '#f1f5f9'};
    transition: background 0.22s;

    svg {
      width: 19px;
      height: 19px;
      stroke: ${p => p.selected ? 'white' : '#64748b'};
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      transition: stroke 0.22s;
    }
  }

  .card-title {
    font-size: 0.8rem;
    font-weight: 750;
    color: ${p => p.selected ? '#0c2d48' : '#334155'};
    margin: 0;
  }

  .card-sub {
    font-size: 0.68rem;
    color: #94a3b8;
    margin: 0;
  }

  @media (min-width: ${bp.lg}) {
    padding: 18px 12px;

    .card-icon { width: 40px; height: 40px; svg { width: 20px; height: 20px; } }
    .card-title { font-size: 0.84rem; }
    .card-sub   { font-size: 0.72rem; }
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 11px 13px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.88rem;
  color: #0c2d48;
  background: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  -webkit-appearance: none;
  box-sizing: border-box;
  font-weight: 500;

  &:hover { border-color: #93c5fd; }

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    background: white;
  }

  &::placeholder { color: #94a3b8; font-weight: 400; }

  @media (min-width: ${bp.lg}) {
    font-size: 0.9rem;
    padding: 12px 14px;
    border-radius: 10px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #f0f9ff;
  border: 1.5px solid #bae6fd;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.18s;
  margin-bottom: 16px;
  touch-action: manipulation;

  &:active { background: #e0f2fe; }

  @media (hover: hover) {
    &:hover { background: #e0f2fe; border-color: #7dd3fc; }
  }

  input {
    width: 17px;
    height: 17px;
    cursor: pointer;
    accent-color: #2563eb;
    flex-shrink: 0;
  }

  span {
    font-size: 0.86rem;
    color: #1e3a5f;
    font-weight: 600;
    user-select: none;
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 13px 24px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 750;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22,1,0.36,1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #2563eb, #0369a1);
  color: white;
  box-shadow: 0 4px 14px rgba(37,99,235,0.28);
  letter-spacing: 0.2px;
  touch-action: manipulation;

  svg {
    width: 16px;
    height: 16px;
    stroke: white;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:active:not(:disabled) { transform: scale(0.97); }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 22px rgba(37,99,235,0.34);
    }
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none !important;
  }
`;

// ═══════════════════════════════════════════════════════════════
// SUCCESS CARD
// ═══════════════════════════════════════════════════════════════

const SuccessCard = styled.div`
  margin-top: 22px;
  background: linear-gradient(135deg, #eff6ff, #f0f9ff);
  border: 1.5px solid #93c5fd;
  border-radius: 14px;
  padding: 20px;
  animation: ${scaleIn} 0.4s cubic-bezier(0.22,1,0.36,1);

  @media (min-width: ${bp.lg}) {
    padding: 24px;
  }
`;

const SuccessHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .icon {
    width: 36px;
    height: 36px;
    background: #2563eb;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    animation: ${popIn} 0.4s ease;

    svg {
      width: 17px;
      height: 17px;
      stroke: white;
      fill: none;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .text {
    h4 {
      font-size: 0.98rem;
      font-weight: 800;
      color: #0c2d48;
      margin: 0 0 2px;
    }
    p {
      font-size: 0.8rem;
      color: #2563eb;
      margin: 0;
      font-weight: 600;
    }
  }
`;

const LinkBox = styled.div`
  background: white;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 12px 14px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.76rem;
  color: #1e3a5f;
  word-break: break-all;
  line-height: 1.5;
  margin-bottom: 12px;
  overflow-wrap: anywhere;
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
  touch-action: manipulation;

  svg {
    width: 14px;
    height: 14px;
    stroke: white;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:active { transform: scale(0.96); }

  @media (hover: hover) {
    &:hover { background: #1d4ed8; transform: translateY(-1px); }
  }
`;

const MetaChipsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const MetaChip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.76rem;
  color: #475569;
  font-weight: 600;
  background: rgba(255,255,255,0.7);
  padding: 5px 11px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;

  strong { color: #1d4ed8; font-weight: 750; }
`;

// ═══════════════════════════════════════════════════════════════
// ASSIGNMENTS TABLE
// ═══════════════════════════════════════════════════════════════

const AssignmentsSection = styled.div`
  margin-top: 44px;
  animation: ${fadeUp} 0.5s ease-out 0.2s both;
`;

const SectionHeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  h2 {
    font-size: 1rem;
    font-weight: 800;
    color: #0c2d48;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 18px;
      height: 18px;
      stroke: #2563eb;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  @media (min-width: ${bp.lg}) {
    h2 { font-size: 1.1rem; }
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 14px;
  border: 1.5px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  min-width: 560px;
  font-size: 0.84rem;

  th, td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
  }

  th {
    background: linear-gradient(90deg, #1e40af, #0369a1);
    font-weight: 700;
    color: rgba(255,255,255,0.92);
    font-size: 0.72rem;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  tbody tr {
    transition: background 0.15s;
    &:last-child td { border-bottom: none; }
    &:active { background: #f8fbff; }
    @media (hover: hover) { &:hover { background: #f8fbff; } }
  }

  code {
    background: #eff6ff;
    padding: 2px 7px;
    border-radius: 4px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 0.8rem;
    color: #1d4ed8;
    font-weight: 700;
  }

  @media (min-width: ${bp.lg}) {
    font-size: 0.88rem;

    th, td { padding: 13px 16px; }
    th { font-size: 0.75rem; }
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 750;
  text-transform: capitalize;
  background: ${p => ({
    completed: '#d1fae5',
    started:   '#fed7aa',
    pending:   '#dbeafe',
    expired:   '#fee2e2',
  }[p.status] || '#f3f4f6')};
  color: ${p => ({
    completed: '#047857',
    started:   '#d97706',
    pending:   '#1d4ed8',
    expired:   '#dc2626',
  }[p.status] || '#6b7280')};
`;

const TableCopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1.5px solid #e2e8f0;
  padding: 5px 11px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.76rem;
  color: #475569;
  font-weight: 700;
  transition: all 0.18s;
  white-space: nowrap;
  touch-action: manipulation;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:active { background: #eff6ff; color: #2563eb; }

  @media (hover: hover) {
    &:hover { background: #f1f5f9; border-color: #cbd5e1; color: #2563eb; }
  }
`;

const NoDataMsg = styled.div`
  text-align: center;
  padding: 36px 20px;
  color: #94a3b8;
  font-size: 0.88rem;
  font-weight: 500;
`;

// ═══════════════════════════════════════════════════════════════
// ANIMATED SVG ICONS
// ═══════════════════════════════════════════════════════════════

// Animated send icon
const AnimatedSendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="22" y1="2" x2="11" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 2,-2; 0,0" dur="1.6s" repeatCount="indefinite" />
    </line>
    <polygon points="22 2 15 22 11 13 2 9 22 2" fill="white" opacity="0.9">
      <animate attributeName="opacity" values="0.9;1;0.9" dur="1.6s" repeatCount="indefinite" />
    </polygon>
  </svg>
);

// Animated loader
const AnimatedLoader = () => (
  <svg viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
    <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2.5"
      strokeLinecap="round" strokeDasharray="28 56"
      style={{ transformOrigin: 'center', animation: 'spin 1s linear infinite' }} />
  </svg>
);

// Animated clipboard icon for panel header
const AnimatedClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
      stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="8" y="2" width="8" height="4" rx="1"
      stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="11" x2="16" y2="11"
      stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="x2" values="16;13;16" dur="2s" repeatCount="indefinite" />
    </line>
    <line x1="12" y1="15" x2="16" y2="15"
      stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Animated link icon for form title
const AnimatedLinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
      stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 1,-1; 0,0" dur="2s" repeatCount="indefinite" />
    </path>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
      stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; -1,1; 0,0" dur="2s" repeatCount="indefinite" />
    </path>
  </svg>
);

// Animated check icon
const AnimatedCheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <polyline points="20 6 9 17 4 12"
      stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
      strokeDasharray="24" strokeDashoffset="0">
      <animate attributeName="stroke-dashoffset"
        values="24;0" dur="0.4s" fill="freeze" />
    </polyline>
  </svg>
);

// Animated copy icon
const AnimatedCopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="9" y="9" width="13" height="13" rx="2"
      stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="opacity" values="1;0.7;1" dur="1.5s" repeatCount="indefinite" />
    </rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
      stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Animated user icon
const AnimatedUserIcon = ({ selected }) => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
      stroke={selected ? 'white' : '#64748b'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {selected && (
        <animate attributeName="stroke-opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
      )}
    </path>
    <circle cx="12" cy="7" r="4"
      stroke={selected ? 'white' : '#64748b'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Animated building icon
const AnimatedBuildingIcon = ({ selected }) => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="4" y="2" width="16" height="20" rx="2"
      stroke={selected ? 'white' : '#64748b'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {[6, 10, 14].map((y, i) => (
      <line key={i} x1="8" y1={y} x2="8.01" y2={y}
        stroke={selected ? 'white' : '#64748b'}
        strokeWidth="2.5" strokeLinecap="round">
        {selected && (
          <animate attributeName="opacity"
            values="1;0.4;1" dur={`${1.2 + i * 0.3}s`} repeatCount="indefinite" />
        )}
      </line>
    ))}
    {[6, 10, 14].map((y, i) => (
      <line key={`r${i}`} x1="16" y1={y} x2="16.01" y2={y}
        stroke={selected ? 'white' : '#64748b'}
        strokeWidth="2.5" strokeLinecap="round">
        {selected && (
          <animate attributeName="opacity"
            values="1;0.4;1" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
        )}
      </line>
    ))}
    <path d="M9 22v-4h6v4"
      stroke={selected ? 'white' : '#64748b'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Animated merge-style icon for "MSE" category badge
const AnimatedMseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
    <circle cx="12" cy="12" r="4" stroke="#7c3aed" strokeWidth="2">
      <animate attributeName="r" values="4;5;4" dur="2s" repeatCount="indefinite" />
    </circle>
    <circle cx="12" cy="12" r="9" stroke="#7c3aed" strokeWidth="1.5"
      strokeDasharray="4 4" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate"
        values="0 12 12;360 12 12" dur="8s" repeatCount="indefinite" />
    </circle>
  </svg>
);

// Animated history / recent links icon
const AnimatedHistoryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <polyline points="1 4 1 10 7 10"
      stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.51 15a9 9 0 1 0 .49-4.5"
      stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animateTransform attributeName="transform" type="rotate"
        values="0 12 12;-20 12 12;0 12 12" dur="3s" repeatCount="indefinite" />
    </path>
    <polyline points="12 7 12 12 16 14"
      stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Animated search icon for Disorders category
const AnimatedSearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="8"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="r" values="8;8.5;8" dur="2s" repeatCount="indefinite" />
    </circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0.5,0.5; 0,0" dur="2s" repeatCount="indefinite" />
    </line>
  </svg>
);

// Animated brain icon for CBT
const AnimatedBrainIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M9.5 2A5.5 5.5 0 0 0 4 7.5c0 1.28.44 2.46 1.17 3.39"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="stroke-opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
    </path>
    <path d="M14.5 2A5.5 5.5 0 0 1 20 7.5c0 1.28-.44 2.46-1.17 3.39"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="stroke-opacity" values="1;0.4;1" dur="2.3s" repeatCount="indefinite" />
    </path>
    <path d="M4.5 12.5A4.5 4.5 0 0 0 4 14.5 4.5 4.5 0 0 0 8.5 19"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M19.5 12.5a4.5 4.5 0 0 1 .5 2 4.5 4.5 0 0 1-4.5 4.5"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8.5 19a4.5 4.5 0 0 0 3.5 3 4.5 4.5 0 0 0 3.5-3"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 2v20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Generic animated category icons (personality, wellbeing, etc.)
const AnimatedPersonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2">
      <animate attributeName="cy" values="7;6.5;7" dur="2s" repeatCount="indefinite" />
    </circle>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AnimatedStarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.5;1" dur="1.8s" repeatCount="indefinite" />
    </polygon>
  </svg>
);

const AnimatedUsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2">
      <animate attributeName="r" values="4;4.3;4" dur="2s" repeatCount="indefinite" />
    </circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AnimatedShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
    </path>
  </svg>
);

const AnimatedHeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.4;1" dur="1.4s" repeatCount="indefinite" />
    </path>
  </svg>
);

const AnimatedScaleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M3 6l4 8H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0,1; 0,0" dur="2s" repeatCount="indefinite" />
    </path>
    <path d="M21 6l-4 8h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0,-1; 0,0" dur="2s" repeatCount="indefinite" />
    </path>
    <line x1="3" y1="21" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const AnimatedMiscIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
    </rect>
  </svg>
);

// Hero illustration
const HeroIllustration = () => (
  <svg viewBox="0 0 180 150" fill="none">
    <rect x="18" y="28" width="62" height="82" rx="8"
      fill="white" fillOpacity="0.12"
      stroke="white" strokeOpacity="0.28" strokeWidth="1.5">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0,-4; 0,0" dur="4s" repeatCount="indefinite" />
    </rect>
    {[48, 58, 68].map((y, i) => (
      <line key={i} x1="30" y1={y} x2={62 - i * 5} y2={y}
        stroke="white" strokeOpacity={0.4 - i * 0.08}
        strokeWidth="2" strokeLinecap="round">
        {i === 0 && (
          <animate attributeName="x2" values="62;52;62" dur="3s" repeatCount="indefinite" />
        )}
      </line>
    ))}
    <circle cx="36" cy="83" r="3" fill="#67e8f9" fillOpacity="0.7">
      <animate attributeName="r" values="3;4.2;3" dur="2s" repeatCount="indefinite" />
    </circle>
    <line x1="43" y1="83" x2="66" y2="83"
      stroke="#67e8f9" strokeOpacity="0.45" strokeWidth="2" strokeLinecap="round" />

    {/* Connecting paths */}
    <path d="M83 68 C103 68, 98 53, 118 53"
      stroke="#67e8f9" strokeOpacity="0.55" strokeWidth="1.5"
      strokeDasharray="4 4" fill="none">
      <animate attributeName="stroke-dashoffset" values="0;-8" dur="1s" repeatCount="indefinite" />
    </path>
    <path d="M83 68 C103 68, 98 83, 118 83"
      stroke="#67e8f9" strokeOpacity="0.55" strokeWidth="1.5"
      strokeDasharray="4 4" fill="none">
      <animate attributeName="stroke-dashoffset" values="0;-8" dur="1.2s" repeatCount="indefinite" />
    </path>

    {/* Person 1 */}
    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0,-3; 0,0" dur="3.5s" repeatCount="indefinite" />
      <circle cx="133" cy="46" r="8"
        fill="white" fillOpacity="0.13"
        stroke="white" strokeOpacity="0.32" strokeWidth="1.5" />
      <circle cx="133" cy="43" r="3" fill="white" fillOpacity="0.55" />
      <path d="M127 53 C127 49, 139 49, 139 53"
        stroke="white" strokeOpacity="0.42" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </g>

    {/* Person 2 */}
    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0,3; 0,0" dur="4s" repeatCount="indefinite" />
      <circle cx="133" cy="80" r="8"
        fill="white" fillOpacity="0.13"
        stroke="white" strokeOpacity="0.32" strokeWidth="1.5" />
      <circle cx="133" cy="77" r="3" fill="white" fillOpacity="0.55" />
      <path d="M127 87 C127 83, 139 83, 139 87"
        stroke="white" strokeOpacity="0.42" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </g>

    {/* Check circle */}
    <circle cx="154" cy="46" r="6" fill="#67e8f9" fillOpacity="0.28">
      <animate attributeName="fillOpacity" values="0.28;0.6;0.28" dur="2s" repeatCount="indefinite" />
    </circle>
    <path d="M151 46 L153 48 L157 44"
      stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
    </path>
  </svg>
);

const HeroBgSvg = () => (
  <svg viewBox="0 0 800 200" preserveAspectRatio="none">
    <defs>
      <pattern id="hGrid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeOpacity="0.04" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#hGrid)" />
    <circle cx="660" cy="40"  r="65" fill="white" fillOpacity="0.03" />
    <circle cx="710" cy="145" r="42" fill="white" fillOpacity="0.04" />
    <circle cx="90"  cy="160" r="85" fill="white" fillOpacity="0.02" />
  </svg>
);

const getCategoryIcon = (catId) => {
  switch (catId) {
    case 'disorders':   return <AnimatedSearchIcon />;
    case 'cbt':         return <AnimatedBrainIcon />;
    case 'personality': return <AnimatedPersonIcon />;
    case 'wellbeing':   return <AnimatedStarIcon />;
    case 'social':      return <AnimatedUsersIcon />;
    case 'resilience':  return <AnimatedShieldIcon />;
    case 'mood':        return <AnimatedHeartIcon />;
    case 'development': return <AnimatedScaleIcon />;
    default:            return <AnimatedMiscIcon />;
  }
};

// Chevron SVG
const ChevronSvg = () => (
  <svg viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// CATEGORY MAP  (MSE removed from disorders)
// ═══════════════════════════════════════════════════════════════

const MSE_TEST_NAME = 'Mental State Examination';

const TEST_CATEGORY_MAP = {
  disorders: {
    name: 'Assessment of Specific Disorders',
    testNames: [
      'Patient Health Questionnaire - 9 Items',
      'Depression Anxiety Stress Scales - 21 Items',
      'Depression Anxiety Stress Scale (21-item)',
      'Edinburgh Postnatal Depression Scale',
      'Generalized Anxiety Disorder - 7 Items',
      'Prolonged Grief Disorder Scale - Revised',
      'PTSD Checklist for DSM-5',
      'Impact of Event Scale – Revised',
      'Life Events Checklist for DSM-5',
      'Adverse Childhood Experiences Questionnaire',
      'PTSD Checklist – Civilian Version',
      'PTSD Checklist – Military Version',
      'Primary Care PTSD Screen for DSM-5',
      'Fast Alcohol Screening Test',
      'Alcohol Use Disorders Identification Test',
      'CAGE Substance Abuse Screening Tool',
      'Clinical Institute Withdrawal Assessment for Alcohol – Revised',
      'Clinical Opiate Withdrawal Scale',
      'Michigan Alcohol Screening Test',
      'Autism Spectrum Quotient',
      'Adult ADHD Self-Report Scale (WHO ASRS-v1.1)',
      'Brief Psychiatric Rating Scale',
      'Strengths and Difficulties Questionnaire',
      'Vanderbilt ADHD Diagnostic Rating Scale (parent-report)',
      'Pediatric Symptom Checklist — 17',
      'Student Risk Screening Scale — Internalizing and Externalizing',
      'Beck Depression Inventory — Second Edition',
      'Patient Health Questionnaire — Adolescent Version',
      "Children's Depression Inventory 2 (Self-Report Full Length)",
      "Revised Children's Anxiety and Depression Scale (47-item Child Self-Report)",
      'Screen for Child Anxiety Related Emotional Disorders (Child Self-Report)',
      'Systematic Screening for Behavior Disorders',
      'Child PTSD Symptom Scale',
      'Child and Adolescent Trauma Screen',
      'Traumatic Events Screening Inventory for Children',
      'CRAFFT Adolescent Substance Use Screen (CRAFFT 2.1)',
      'Revised Child Anxiety and Depression Scale – 25 Items (RCADS-25)',
    ],
  },
  cbt: {
    name: 'CBT-Informed Assessments',
    testNames: [
      'African-Adapted Cognitive Distortions Scale',
      'Automatic Thoughts Questionnaire',
      'Maladaptive Schema Scale - Young Schema Questionnaire Aligned',
    ],
  },
  personality: {
    name: 'Personality Assessment',
    testNames: [
      'Five Factor Personality Scale - 30 Items',
      'Personality Inventory for DSM-5 - Short Form',
      'IPIP Big Five Factor Markers — 50-Item',
      'Mini-IPIP — 20-Item Big Five',
      'IPIP-NEO-120',
      'IPIP-HEXACO Personality Inventory',
      'IPIP-BIS/BAS (Behavioral Inhibition/Activation Systems)',
    ],
  },
  wellbeing: {
    name: 'Well-Being & Self-Esteem',
    testNames: [
      'Rosenberg Self-Esteem Scale',
      'Coopersmith Self-Esteem Inventory',
      'Warwick-Edinburgh Mental Well-Being Scale',
      'Satisfaction With Life Scale',
      'Positive and Negative Affect Schedule',
      'State Self-Esteem Scale',
      'YSR/CBCL Competence Scales',
      'Self-Perception Profile for Children',
      "Piers-Harris Children's Self-Concept Scale",
      'PROMIS Anger Short Form 5a',
      'WHO-5 Well-Being Index',
      'Work and Social Adjustment Scale',
      'Sheehan Disability Scale',
      'Global Assessment of Functioning',
      'Schutte Self-Report Emotional Intelligence Test',
      'Wong and Law Emotional Intelligence Scale',
      'Trait Emotional Intelligence Questionnaire — Short Form',
      'Student Life Satisfaction Scale',
    ],
  },
  social: {
    name: 'Social Support & Loneliness',
    testNames: [
      'UCLA Loneliness Scale — Version 3',
      'Multidimensional Scale of Perceived Social Support',
      'De Jong Gierveld Loneliness Scale',
      "Children's Loneliness Scale",
    ],
  },
  resilience: {
    name: 'Resilience & Coping',
    testNames: [
      'Resilience Scale for Adolescents',
      'Brief Resilience Scale',
      'Connor-Davidson Resilience Scale',
      'Child and Youth Resilience Measure',
      'Adolescent Coping Scale',
      'COPE Inventory',
    ],
  },
  mood: {
    name: 'Mood & Emotional Assessment',
    testNames: [
      "Children's Hope Scale",
      'Mood and Feelings Questionnaire',
      'Mood Disorder Questionnaire',
      'Altman Self-Rating Mania Scale',
      'Young Mania Rating Scale',
      'Bipolar Spectrum Diagnostic Scale',
      'Rapid Mood Screener',
      'Montgomery-Åsberg Depression Rating Scale',
      'Hamilton Rating Scale for Depression (HDRS-17)',
      'Functional Assessment Short Test — Bipolar Disorder',
      'Difficulties in Emotion Regulation Scale (DERS)',
    ],
  },
  development: {
    name: 'Identity & Developmental',
    testNames: [
      'Ego Identity Process Questionnaire',
      'Erikson Psychosocial Stage Inventory',
      'Objective Measure of Ego Identity Status',
      'Empathy Quotient',
      'Chalder Fatigue Scale (CFQ-11)',
    ],
  },
  other: {
    name: 'Other Available Tools',
    testNames: [
      'Columbia Suicide Severity Rating Scale',
      'Transgression-Related Interpersonal Motivations Inventory (TRIM-18)',
      'Heartland Forgiveness Scale',
      'Experiences in Close Relationships – Revised',
      'Child-Parent Relationship Scale',
      'Maslach Burnout Inventory (MBI)',
      'Utrecht Work Engagement Scale – 17 (UWES-17)',
    ],
  },
};

const categorizeTests = (tests) => {
  // MSE is handled separately — exclude from all category maps
  const mseTest = tests.find(t => t.name === MSE_TEST_NAME);

  const categoryEntries = Object.entries(TEST_CATEGORY_MAP).map(([catId, catInfo]) => ({
    id: catId,
    name: catInfo.name,
    tests: tests.filter(
      t => t.name !== MSE_TEST_NAME && catInfo.testNames.includes(t.name)
    ),
  }));

  const categorizedIds = new Set([
    ...(mseTest ? [mseTest.id] : []),
    ...categoryEntries.flatMap(c => c.tests.map(t => t.id)),
  ]);

  const uncategorized = tests.filter(t => !categorizedIds.has(t.id));

  if (uncategorized.length > 0) {
    const existingOther = categoryEntries.find(c => c.id === 'other');
    if (existingOther) {
      existingOther.tests = [...existingOther.tests, ...uncategorized];
    } else {
      categoryEntries.push({
        id: 'other',
        name: 'Other Available Tools',
        tests: uncategorized,
      });
    }
  }

  return { mseTest: mseTest || null, categories: categoryEntries };
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

const SendTest = () => {
  const [allTests, setAllTests]                   = useState([]);
  const [mseTest, setMseTest]                     = useState(null);
  const [testCategories, setTestCategories]       = useState([]);
  const [expandedCats, setExpandedCats]           = useState(['disorders']);
  const [searchTerm, setSearchTerm]               = useState('');
  const [searchQuery, setSearchQuery]             = useState('');
  const [selectedIds, setSelectedIds]             = useState([]);
  const [selectedNames, setSelectedNames]         = useState([]);
  const [selectedPrices, setSelectedPrices]       = useState([]);
  const [linkType, setLinkType]                   = useState('individual');
  const [clientName, setClientName]               = useState('');
  const [clientEmail, setClientEmail]             = useState('');
  const [clientWhatsapp, setClientWhatsapp]       = useState('');
  const [orgName, setOrgName]                     = useState('');
  const [recipientCount, setRecipientCount]       = useState(1);
  const [sendEmail, setSendEmail]                 = useState(true);
  const [loading, setLoading]                     = useState(false);
  const [success, setSuccess]                     = useState(null);
  const [showForm, setShowForm]                   = useState(false);
  const [assignments, setAssignments]             = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const formRef = useRef(null);
  const pendingWhatsAppWindowRef = useRef(null);

  useEffect(() => { loadTests(); loadAssignments(); }, []);

  useEffect(() => {
    if (selectedIds.length > 0) {
      setShowForm(true);
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 180);
    }
  }, [selectedIds.length]);

  const loadTests = async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/tests`);
      const tests = res.data;
      setAllTests(tests);
      const { mseTest: mse, categories } = categorizeTests(tests);
      setMseTest(mse);
      setTestCategories(categories);
    } catch {
      toast.error('Failed to load tests');
    }
  };

  const loadAssignments = async () => {
    try {
      setLoadingAssignments(true);
      const res = await axios.get(`${API_URL}/practitioner/assignments?limit=50`);
      setAssignments(res.data.assignments || []);
    } catch (e) {
      console.error('Failed to load assignments:', e);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const toggleCategory = (catId) =>
    setExpandedCats(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );

  const handleSelect = (testId, testName, testPrice = 0) => {
    const isSelected = selectedIds.includes(testId);
    setSelectedIds(prev =>
      isSelected ? prev.filter(id => id !== testId) : [...prev, testId]
    );
    setSelectedNames(prev =>
      isSelected ? prev.filter(n => n !== testName) : [...prev, testName]
    );
    setSelectedPrices(prev =>
      isSelected
        ? prev.filter(t => t.id !== testId)
        : [...prev, { id: testId, price: parseFloat(testPrice) || 0 }]
    );
    setSuccess(null);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedNames([]);
    setSelectedPrices([]);
    setShowForm(false);
    setSuccess(null);
  };

  const totalPrice = selectedPrices.reduce((s, t) => s + (t.price || 0), 0);
  const hasSelection = selectedIds.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) { toast.error('Please select at least one test'); return; }
    if (linkType === 'individual' && !clientName) {
      toast.error('Please enter client name'); return;
    }
    if (linkType === 'organization' && (!orgName || recipientCount < 1)) {
      toast.error('Please enter organization name and recipient count'); return;
    }

    const whatsappPhone = sanitizePhoneForWhatsApp(clientWhatsapp);
    const shouldOpenWhatsApp = linkType === 'individual' && Boolean(whatsappPhone);
    if (shouldOpenWhatsApp) {
      pendingWhatsAppWindowRef.current = window.open('', '_blank');
    }

    setLoading(true);
    try {
      const payload = { testIds: selectedIds, linkType };
      if (linkType === 'individual') {
        payload.clientName  = clientName;
        payload.clientEmail = clientEmail || null;
        payload.clientPhone = whatsappPhone || null;
        payload.sendEmail   = sendEmail && Boolean(clientEmail?.trim());
      } else {
        payload.organizationName = orgName;
        payload.recipientCount   = parseInt(recipientCount);
      }
      const res = await axios.post(`${API_URL}/practitioner/assignments`, payload);
      const createdAssignment = {
        ...res.data.assignment,
        clientPhone: res.data.assignment.clientPhone || whatsappPhone,
      };
      setSuccess(createdAssignment);

      if (shouldOpenWhatsApp) {
        openWhatsApp({
          phone: whatsappPhone,
          link: createdAssignment.testLink,
          client: createdAssignment.clientName || clientName,
          targetWindow: pendingWhatsAppWindowRef.current,
        });
        pendingWhatsAppWindowRef.current = null;
      }

      const emailWillBeSent = linkType === 'individual' && sendEmail && Boolean(clientEmail?.trim());
      toast.success(shouldOpenWhatsApp
        ? 'Link created and WhatsApp opened!'
        : (emailWillBeSent ? 'Link created & email sent!' : 'Link created successfully!')
      );
      setClientName(''); setClientEmail(''); setClientWhatsapp(''); setOrgName(''); setRecipientCount(1);
      loadAssignments();
    } catch (err) {
      pendingWhatsAppWindowRef.current?.close?.();
      pendingWhatsAppWindowRef.current = null;
      toast.error(err.response?.data?.error || 'Failed to create test link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  const sanitizePhoneForWhatsApp = (phone) => (phone || '').replace(/[^\d]/g, '');

  const buildWhatsAppUrl = ({ phone, link, client }) => {
    const normalizedPhone = sanitizePhoneForWhatsApp(phone);
    if (!normalizedPhone) return null;
    const message = encodeURIComponent(
      `Hello ${client || ''}, here is your psychology assessment link: ${link}`.trim()
    );
    return `https://wa.me/${normalizedPhone}?text=${message}`;
  };

  const openWhatsApp = ({ phone, link, client, targetWindow = null }) => {
    const whatsappUrl = buildWhatsAppUrl({ phone, link, client });
    if (!whatsappUrl) {
      toast.error('Please enter a WhatsApp number including country code (e.g. 15551234567)');
      targetWindow?.close?.();
      return false;
    }

    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = whatsappUrl;
    } else {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
    return true;
  };

  const totalTests = testCategories.reduce((s, c) => s + c.tests.length, 0)
    + (mseTest ? 1 : 0);

  const searchKey = searchQuery.trim().toLowerCase();
  const filteredMseTest = !mseTest ? null : (!searchKey
    || mseTest.name.toLowerCase().includes(searchKey)
    || (mseTest.description || '').toLowerCase().includes(searchKey)
      ? mseTest
      : null);

  const filteredCategories = !searchKey
    ? testCategories
    : testCategories
        .map(cat => ({
          ...cat,
          tests: cat.tests.filter(test =>
            test.name.toLowerCase().includes(searchKey)
            || (test.description || '').toLowerCase().includes(searchKey)
          ),
        }))
        .filter(cat => cat.tests.length > 0);

  return (
    <PageWrapper>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Container>

        {/* ── Hero ── */}
        <HeroSection>
          <HeroBg><HeroBgSvg /></HeroBg>
          <HeroGrid>
            <HeroLeft>
              <HeroBadge>
                <BadgeDot />
                <BadgeText>Assessment Distribution</BadgeText>
              </HeroBadge>
              <HeroTitle>Send Assessment</HeroTitle>
              <HeroDesc>
                Create secure links to distribute psychological assessments
                to individual clients or entire organisations.
              </HeroDesc>
            </HeroLeft>
            <HeroVisual><HeroIllustration /></HeroVisual>
          </HeroGrid>
        </HeroSection>

        {/* ── Step indicator ── */}
        <StepRow>
          <StepItem active={!hasSelection} completed={hasSelection}>
            <span className="num">
              {hasSelection
                ? <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                : '1'}
            </span>
            <span className="label">Select Assessment</span>
          </StepItem>
          <StepLine active={hasSelection} />
          <StepItem active={hasSelection && !success} completed={!!success}>
            <span className="num">
              {success
                ? <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                : '2'}
            </span>
            <span className="label">Create Link</span>
          </StepItem>
        </StepRow>

        {/* ── Available Assessments panel ── */}
        <TestsPanel>
          <TestsPanelHeader>
            <PanelTitle>
              <span className="icon-wrap"><AnimatedClipboardIcon /></span>
              Available Assessments
            </PanelTitle>
            <CountBadge>{totalTests} tools</CountBadge>
          </TestsPanelHeader>

          <SearchBar>
            <SearchInput
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tests/tools by name or description"
              onKeyDown={(e) => { if (e.key === 'Enter') setSearchQuery(searchTerm); }}
            />
            <SearchBtn
              type="button"
              onClick={() => setSearchQuery(searchTerm)}
              disabled={searchTerm.trim() === searchQuery.trim()}
            >
              Search
            </SearchBtn>
          </SearchBar>

          {/* ── MSE standalone ── */}
          {filteredMseTest && (
            <MseBlock>
              <MseHeader>
                <AnimatedMseIcon />
                <span className="lbl">Mental State Examination</span>
                <span className="line" />
              </MseHeader>
              <MseRow
                selected={selectedIds.includes(filteredMseTest.id)}
                onClick={() => handleSelect(filteredMseTest.id, filteredMseTest.name, filteredMseTest.price)}
              >
                <div className="info">
                  <p className="name">
                    {filteredMseTest.name}
                    <MseBadge>Standalone</MseBadge>
                  </p>
                  <p className="desc">
                    {filteredMseTest.description || 'Comprehensive mental state assessment tool'}
                  </p>
                </div>
                {filteredMseTest.price && (
                  <span className="price">
                    ₵{typeof filteredMseTest.price === 'number'
                      ? filteredMseTest.price.toFixed(2)
                      : filteredMseTest.price}
                  </span>
                )}
              </MseRow>
            </MseBlock>
          )}

          {/* ── Category accordion ── */}
          {filteredCategories.length > 0
            ? filteredCategories.map(cat => (
                <CategoryBlock key={cat.id}>
                  <CategoryHead
                    catId={cat.id}
                    expanded={expandedCats.includes(cat.id)}
                    onClick={() => toggleCategory(cat.id)}
                  >
                    <CatLeft>
                      <CatIcon catId={cat.id} expanded={expandedCats.includes(cat.id)}>
                        {getCategoryIcon(cat.id)}
                      </CatIcon>
                      <CatText expanded={expandedCats.includes(cat.id)}>
                        <p className="cat-name">{cat.name}</p>
                        <p className="cat-count">
                          {cat.tests.length} {cat.tests.length === 1 ? 'tool' : 'tools'}
                        </p>
                      </CatText>
                    </CatLeft>
                    <Chevron expanded={expandedCats.includes(cat.id)}>
                      <ChevronSvg />
                    </Chevron>
                  </CategoryHead>

                  <CategoryBody expanded={expandedCats.includes(cat.id)}>
                    {cat.tests.map(test => (
                      <TestRow
                        key={test.id}
                        selected={selectedIds.includes(test.id)}
                        onClick={() => handleSelect(test.id, test.name, test.price)}
                      >
                        <div className="test-info">
                          <p className="test-name">{test.name}</p>
                          <p className="test-desc">
                            {test.description || 'Assessment tool'}
                          </p>
                        </div>
                        {test.price && (
                          <span className="test-price">
                            ₵{typeof test.price === 'number'
                              ? test.price.toFixed(2)
                              : test.price}
                          </span>
                        )}
                      </TestRow>
                    ))}
                  </CategoryBody>
                </CategoryBlock>
              ))
            : (
              <NoDataMsg>{searchKey ? 'No assessments match your search.' : 'Loading assessments…'}</NoDataMsg>
            )}
        </TestsPanel>

        {/* ── Connector ── */}
        <ConnectorWrap visible={hasSelection}>
          <ConnectorLine />
          <ConnectorLabel>Configure &amp; Send</ConnectorLabel>
        </ConnectorWrap>

        {/* ── Form panel ── */}
        <div ref={formRef}>
          <FormPanelWrapper visible={showForm && hasSelection}>
            <FormPanel>
              <FormSectionTitle>
                <span className="icon-wrap"><AnimatedLinkIcon /></span>
                Create Test Link
              </FormSectionTitle>

              <form onSubmit={handleSubmit}>
                {/* Selected banner */}
                <SelectedBanner>
                  <span className="check-icon"><AnimatedCheckIcon /></span>
                  <div className="text">
                    <p className="label">
                      Selected ({selectedIds.length})
                    </p>
                    <p className="name">
                      {selectedIds.length === 1
                        ? selectedNames[0]
                        : `${selectedIds.length} assessments selected`}
                    </p>
                    {selectedIds.length > 1 && (
                      <div className="sub-list">
                        {selectedNames.slice(0, 3).map((n, i) => (
                          <div key={i}>• {n}</div>
                        ))}
                        {selectedIds.length > 3 && (
                          <div>• +{selectedIds.length - 3} more</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="price-wrap">
                    <p className="price-lbl">Total</p>
                    <p className="price-val">₵{totalPrice.toFixed(2)}</p>
                  </div>
                  <button type="button" className="change-btn" onClick={clearSelection}>
                    Change
                  </button>
                </SelectedBanner>

                {/* Distribution type */}
                <FormGroup delay="0.04s">
                  <FieldLabel>Distribution Type</FieldLabel>
                  <RadioCards>
                    <RadioCard selected={linkType === 'individual'}>
                      <input type="radio" value="individual"
                        checked={linkType === 'individual'}
                        onChange={e => setLinkType(e.target.value)} />
                      <div className="card-icon">
                        <AnimatedUserIcon selected={linkType === 'individual'} />
                      </div>
                      <p className="card-title">Individual</p>
                      <p className="card-sub">One person</p>
                    </RadioCard>
                    <RadioCard selected={linkType === 'organization'}>
                      <input type="radio" value="organization"
                        checked={linkType === 'organization'}
                        onChange={e => setLinkType(e.target.value)} />
                      <div className="card-icon">
                        <AnimatedBuildingIcon selected={linkType === 'organization'} />
                      </div>
                      <p className="card-title">Organization</p>
                      <p className="card-sub">Multiple people</p>
                    </RadioCard>
                  </RadioCards>
                </FormGroup>

                {linkType === 'individual' ? (
                  <>
                    <FormGroup delay="0.08s">
                      <FieldLabel>Client Name <span className="req">*</span></FieldLabel>
                      <InputField
                        type="text"
                        value={clientName}
                        onChange={e => setClientName(e.target.value)}
                        placeholder="Full name"
                        required
                      />
                    </FormGroup>
                    <FormGroup delay="0.12s">
                      <FieldLabel>Client Email</FieldLabel>
                      <InputField
                        type="email"
                        value={clientEmail}
                        onChange={e => setClientEmail(e.target.value)}
                        placeholder="email@example.com"
                      />
                    </FormGroup>
                    <FormGroup delay="0.16s">
                      <FieldLabel>Client WhatsApp (with country code)</FieldLabel>
                      <InputField
                        type="tel"
                        value={clientWhatsapp}
                        onChange={e => setClientWhatsapp(e.target.value)}
                        placeholder="15551234567"
                      />
                    </FormGroup>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={e => setSendEmail(e.target.checked)}
                      />
                      <span>Send test link via email</span>
                    </CheckboxLabel>
                  </>
                ) : (
                  <>
                    <FormGroup delay="0.08s">
                      <FieldLabel>Organisation Name <span className="req">*</span></FieldLabel>
                      <InputField
                        type="text"
                        value={orgName}
                        onChange={e => setOrgName(e.target.value)}
                        placeholder="Organisation name"
                        required
                      />
                    </FormGroup>
                    <FormGroup delay="0.12s">
                      <FieldLabel>Expected Recipients <span className="req">*</span></FieldLabel>
                      <InputField
                        type="number"
                        value={recipientCount}
                        onChange={e => setRecipientCount(e.target.value)}
                        placeholder="Number of participants"
                        min="1"
                        required
                      />
                    </FormGroup>
                  </>
                )}

                <SubmitBtn type="submit" disabled={loading || selectedIds.length === 0}>
                  {loading
                    ? <><AnimatedLoader /> Creating…</>
                    : <><AnimatedSendIcon /> Generate Link</>}
                </SubmitBtn>
              </form>

              {/* Success card */}
              {success && (
                <SuccessCard>
                  <SuccessHeader>
                    <span className="icon"><AnimatedCheckIcon /></span>
                    <div className="text">
                      <h4>Link Created!</h4>
                      <p>
                        {success.testsCount > 1
                          ? `${success.testsCount} assessments ready`
                          : `Ready for ${success.clientName || success.organizationName}`}
                      </p>
                    </div>
                  </SuccessHeader>
                  <LinkBox>{success.testLink}</LinkBox>
                  <CopyBtn onClick={() => copyLink(success.testLink)}>
                    <AnimatedCopyIcon /> Copy Link
                  </CopyBtn>
                  {linkType === 'individual' && (
                    <CopyBtn
                      style={{ marginLeft: 10, background: '#16a34a' }}
                      onClick={() => openWhatsApp({
                        phone: success.clientPhone || clientWhatsapp,
                        link: success.testLink,
                        client: success.clientName || clientName,
                      })}
                    >
                      Reopen WhatsApp
                    </CopyBtn>
                  )}
                  <MetaChipsRow>
                    <MetaChip>Code: <strong>{success.uniqueCode}</strong></MetaChip>
                    <MetaChip>
                      Expires: <strong>
                        {new Date(success.expiresAt).toLocaleDateString()}
                      </strong>
                    </MetaChip>
                  </MetaChipsRow>
                </SuccessCard>
              )}
            </FormPanel>
          </FormPanelWrapper>
        </div>

        {/* ── Recent test links ── */}
        {(loadingAssignments || assignments.length > 0) && (
          <AssignmentsSection>
            <SectionHeadingRow>
              <h2>
                <AnimatedHistoryIcon />
                Recent Test Links
              </h2>
            </SectionHeadingRow>

            {loadingAssignments ? (
              <NoDataMsg>Loading your test links…</NoDataMsg>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Client / Organisation</th>
                      <th>Test</th>
                      <th>Code</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.slice(0, 15).map(a => {
                      const link = `${process.env.REACT_APP_CLIENT_URL || 'https://psych-client.onrender.com'}/#/test/${a.uniqueCode}`;
                      return (
                        <tr key={a.id}>
                          <td>
                            <strong>{a.clientName || a.organizationName}</strong>
                            {a.clientEmail && (
                              <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 2 }}>
                                {a.clientEmail}
                              </div>
                            )}
                            {a.recipientCount && (
                              <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 2 }}>
                                {a.recipientCount} participant{a.recipientCount !== 1 ? 's' : ''}
                              </div>
                            )}
                          </td>
                          <td>{a.TestCatalog?.shortName || a.TestCatalog?.name}</td>
                          <td><code>{a.uniqueCode}</code></td>
                          <td><StatusBadge status={a.status}>{a.status}</StatusBadge></td>
                          <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <TableCopyBtn onClick={() => copyLink(link)}>
                                <svg viewBox="0 0 24 24">
                                  <rect x="9" y="9" width="13" height="13" rx="2" />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                                Copy
                              </TableCopyBtn>
                              {a.clientPhone && (
                                <TableCopyBtn onClick={() => openWhatsApp({ phone: a.clientPhone, link, client: a.clientName })}>
                                  WhatsApp
                                </TableCopyBtn>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </AssignmentsSection>
        )}

      </Container>
    </PageWrapper>
  );
};

export default SendTest;
